<template>
  <div class="task-time-estimate-li" :class="componentClasses">
    <TaskTimeEstimateInput
      v-if="performerEstimate.IsEdit"
      :taskId="taskId"
      :performerEstimate="performerEstimate"
      :estimate="performerEstimate.Estimate"
      @upsert="upsertEstimate"
      @cancel="toggleEdit(false)"
    />
    <template v-else>
      <div class="task-time-estimate-li_content">
        <TirPmTooltipElement>
          <TirPmIconView
            :size="24"
            isRounded
            :isBordered="false"
            :colorCode="performerEstimate.AvatarColor"
            :imageLoadUrl="performerEstimate.Foto"
          >
            <template #mainSlot>
              {{ computedNameFoto }}
            </template>
          </TirPmIconView>
          <template #content>
            {{ performerEstimate.LastName }} {{ performerEstimate.FirstName }}
            {{ performerEstimate.MiddleName }}
          </template>
        </TirPmTooltipElement>
        <span v-text="estimateText" class="task-time-estimate-li_content-text" />
        <TirPmTooltipElement v-if="upsertTime">
          <InformationCircleIcon class="task-time-estimate-li_content_info" />
          <template #content>
            {{ upsertTime }}
          </template>
        </TirPmTooltipElement>
      </div>
      <div v-if="isEditTime" class="task-time-estimate-li_buttons">
        <TirPmButton
          v-if="estimate"
          :size="TirPmButtonSizeEnum.Small"
          :variant="TirPmButtonVariantEnum.LinkState"
          @click="toggleEdit(true)"
        >
          <template #leftSlot>
            <PencilSquareIcon />
          </template>
        </TirPmButton>
        <TaskTimeEstimateRemove
          :taskId="taskId"
          :performerId="performerEstimate.Id"
          @onRemove="onRemoveInvite"
          @open="setIsPopoverOpen(true)"
          @close="setIsPopoverOpen(false)"
        />
      </div>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { computed, PropType, ref } from 'vue';
import { PerformerEstimatedModel } from '../../models';
import { InformationCircleIcon, PencilSquareIcon } from 'tir-style-system/icons/outline';
import {
  TirPmIconView,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmTooltipElement,
} from 'tir-components';
import TaskTimeEstimateRemove from './task-time-estimate-remove.vue';
import TaskTimeEstimateInput from './task-time-estimate-input.vue';

/**Параметры */
const props = defineProps({
  /**Оценка по исполнителю */
  performerEstimate: { type: Object as PropType<PerformerEstimatedModel>, required: true },
  /**Id задачи */
  taskId: { type: Number },
  /**Редактирование */
  isEditTime: { type: Boolean },
});
/**События */
const emit = defineEmits<{
  /**Удаление оценки времени */
  removePerformer: [performerId: number];
}>();

/**Открыт ли поповер с подтверждением удаления */
const isPopoverOpen = ref();

/**Классы компонента */
const componentClasses = computed(() => ({
  'with-popover': isPopoverOpen.value,
}));
/**Инициалы */
const computedNameFoto = computed(() => {
  return (
    props.performerEstimate.FirstName?.[0]?.toUpperCase() +
    props.performerEstimate.LastName?.[0]?.toUpperCase()
  );
});
/**Оценка */
const estimate = computed(() => props.performerEstimate?.Estimate?.toFixed(2) || 0);
/**Текст оценки времени */
const estimateText = computed(() => (estimate.value ? `${estimate.value}ч.` : '?'));
/**Время установки оценки */
const upsertTime = computed(() => {
  const date = props.performerEstimate.DateUpsert;
  if (!date || !estimate.value) return '';

  const day = addZero(date.getDate());
  const month = addZero(date.getMonth() + 1);
  const year = addZero(date.getFullYear(), 4);
  return `${day}.${month}.${year}`;
});

/**Добавление нуля */
const addZero = (n: number, numberLength: number = 2) => {
  const number = String(n);
  return number.length == numberLength ? number : `${'0'.repeat(numberLength - 1)}${number}`;
};
/**Удаление приглашения */
const onRemoveInvite = () => {
  emit('removePerformer', props.performerEstimate.Id);
};
/**Установка значения поля isPopoverOpen */
const setIsPopoverOpen = (open: boolean) => {
  isPopoverOpen.value = open;
};
/**Редактирование оценки */
const toggleEdit = (isEdit: boolean) => {
  props.performerEstimate.IsEdit = isEdit;
};
/**Обновление оценки */
const upsertEstimate = (estimate: number) => {
  props.performerEstimate.Estimate = estimate;
  props.performerEstimate.DateUpsert = new Date();
};
</script>
<style lang="scss" scoped>
.task-time-estimate-li {
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &_content {
    min-width: 5rem;
    min-height: 2rem;
    padding: 0 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    border-radius: 0.375rem;
    background-color: var(--neutral-translucent-10, rgba(39, 45, 55, 0.1));
    &_info {
      width: 1rem;
      height: 1rem;
      color: var(--text-primary);
      cursor: pointer;
    }
    &_upsert {
      padding: 0 0.25rem;
      height: 2.25rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
  &_content-text {
    color: var(--text-primary, #272d37);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
  }
  &_buttons {
    display: none;
    gap: 0.25rem;
  }
  &:hover,
  &.with-popover {
    .task-time-estimate-li_buttons {
      display: flex;
    }
  }
}
</style>
